import { useMemo, useState } from 'react';
import { kitRegistry } from './registry.jsx';
import { MaxUI } from '@maxhub/max-ui';
import { useColorScheme } from './useColorScheme.jsx'

export function KitPlayground({ component }) {
  const example = kitRegistry[component];
  if (!example) {
    return (
      <div className="kit-playground kit-playground--error">
        Component "{component}" is not registered.
      </div>
    );
  }
  const [platform, setPlatform] = useState(example.defaultPlatform || 'ios');
  const [zoom, setZoom] = useState(example.defaultZoom || 100);
  const [showCode, setShowCode] = useState(false);
  const [props, setProps] = useState(() => {
    return Object.fromEntries(
      (example.controls || []).map((control) => [
        control.name,
        control.default,
      ])
    );
  });
  const Preview = example.Preview;
  const code = useMemo(() => {
    return example.getCode(props);
  }, [example, props]);

  const colorScheme = useColorScheme()
  return (
    <section className="kit-playground" data-platform={platform}>
      <div className="kit-playground__frame">
        <div className="kit-playground__stage">
          <div
            className="kit-playground__viewport"
            style={{ transform: `scale(${zoom / 100})` }}
          >
              <MaxUI className='kit-playground__item' platform={platform} colorScheme={colorScheme}>
                <Preview props={props} platform={platform} colorScheme={colorScheme} />
            </MaxUI>
          </div>
        </div>
        <div className="kit-playground__footer">
          <div className="kit-playground__tools">
            <button
              className="kit-playground__icon-button"
              type="button"
              onClick={() => setZoom((value) => Math.min(value + 25, 300))}
              aria-label="Увеличить"
            >
              <ZoomInIcon/>
            </button>
            <button
              className="kit-playground__icon-button"
              type="button"
              onClick={() => setZoom((value) => Math.max(value - 25, 25))}
              aria-label="Уменьшить"
            >
              <ZoomOutIcon/>
            </button>
            <span className="kit-playground__zoom">{zoom}%</span>
            <div className="kit-playground__platform-group">
              <button
                className="kit-playground__platform"
                type="button"
                aria-pressed={platform === 'android'}
                onClick={() => setPlatform('android')}
              >
                <AndroidIcon/>
              </button>
              <span className='kit-playground__platform-slash'>/</span>
              <button
                className="kit-playground__platform"
                type="button"
                aria-pressed={platform === 'ios'}
                onClick={() => setPlatform('ios')}
              >
                <AppleIcon/>
              </button>
            </div>
          </div>
          <button
            className="kit-playground__show-code"
            type="button"
            aria-expanded={showCode}
            onClick={() => setShowCode((value) => !value)}
          >
            {showCode ? 'Скрыть код' : 'Показать код'}
            <span aria-hidden="true">›</span>
          </button>

        </div>
      {showCode && (
        <div className="kit-playground__code">
          <pre>
            <code>{code}</code>
          </pre>
        </div>
      )}
      </div>
      {example.controls?.length > 0 && (
        <PropsTable
          controls={example.controls}
          props={props}
          onChange={(name, value) => {
            setProps((current) => ({
              ...current,
              [name]: value,
            }));
          }}
        />
      )}
    </section>
  );
}
function PropsTable({ controls, props, onChange }) {
  return (
    <div className="kit-props">
      <div className="kit-props__head">
        <div>Параметр</div>
        <div>Значение</div>
        <div>Выбор значения</div>
      </div>
      {controls.map
((control) => (
        <div className="kit-props__row" key={control.name
}>
          <div className="kit-props__name">{control.label}</div>
          <div className="kit-props__type">
            {Array.isArray(control.tsType)
              ? control.tsType.map((item, i) => (
                  <code key={item + i}>{item}</code>
                ))
              : <code>{control.tsType}</code>}
          </div>
          <div className="kit-props__control">
            <ControlRenderer
              control={control}
              value={props[control.name
]}
              onChange={(value) => onChange(control.name
, value)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
function ControlRenderer({ control, value, onChange }) {
  if (control.type === 'boolean') {
    return (
      <div className="kit-segmented">
        <button
          type="button"
          aria-pressed={value === false}
          onClick={() => onChange(false)}
        >
          False
        </button>
        <button
          type="button"
          aria-pressed={value === true}
          onClick={() => onChange(true)}
        >
          True
        </button>
      </div>
    );
  }
  if (control.type === 'enum') {
    return (
      <div className="kit-radio-group">
        {control.options.map((option) => (
          <label key={option}>
            <input
              type="radio"
              name={control.name}
              checked={value === option}
              onChange={() => onChange(option)}
            />
            {option}
          </label>
        ))}
      </div>
    )
  }
  if (control.type === 'text') {
    return (
      <input
        className="kit-input"
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
      />
    )
  }

  if(control.type === 'static'){
    return (
      <span className='kit-static-value'>
        {control.value ?? '-'}
      </span>
    )
  }
  if (control.type === 'select') {
    return (
      <select
        className="kit-select"
        value={value ?? control.default}
        onChange={(event) => onChange(event.target.value)}
      >
        {control.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    )
  }
  return null;
}

function ZoomInIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M10.75 17.5a6.75 6.75 0 1 1 0-13.5 6.75 6.75 0 0 1 0 13.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M15.75 15.75 20 20"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M10.75 7.75v6M7.75 10.75h6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}
function ZoomOutIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M10.75 17.5a6.75 6.75 0 1 1 0-13.5 6.75 6.75 0 0 1 0 13.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M15.75 15.75 20 20"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M7.75 10.75h6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}
function AndroidIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <g transform="scale(1.5)">
        <path
          fill="currentColor"
          fill-rule="evenodd"
          d="M12.75 2.47a.5.5 0 0 1 .866.5l-1.333 2.308A8 8 0 0 1 16 12.037H0A8 8 0 0 1 3.62 5.34L2.25 2.97a.5.5 0 0 1 .866-.5l1.372 2.376A8 8 0 0 1 8 4.037a8 8 0 0 1 3.407.76zM13 9.037a1 1 0 1 1-2 0 1 1 0 0 1 2 0m-9 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2"
          clip-rule="evenodd"
        />
      </g>
    </svg>
  )
}
function AppleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M16.85 12.7c.02 2.25 1.9 3 1.92 3.01-.02.06-.3 1.05-.98 2.08-.6.9-1.22 1.78-2.2 1.8-.96.02-1.27-.58-2.37-.58s-1.45.56-2.36.6c-.95.03-1.67-.97-2.27-1.86-1.24-1.82-2.19-5.15-.92-7.4.63-1.12 1.76-1.83 2.98-1.85.93-.02 1.8.63 2.37.63.56
 0 1.62-.78 2.73-.66.46.02 1.76.19 2.6 1.42-.07.04-1.52.9-1.5 2.81Z"
        fill="currentColor"
      />
      <path
        d="M15.02 7.25c.5-.6.84-1.44.75-2.25-.72.03-1.6.49-2.11 1.09-.46.53-.87 1.39-.76 2.2.8.06 1.62-.42 2.12-1.04Z"
        fill="currentColor"
      />
    </svg>
  )
}
